'use strict';

var framerMotion = require('framer-motion');
var React = require('react');
var reactUtils = require('@sravni/react-utils');
var Space = require('../../Space/src/Space.js');
var _utils = require('../../Transition/src/_utils.js');
require('../../Transition/src/Fade.js');
require('../../Transition/src/ScaleFade.js');
require('../../Transition/src/SlideFade.js');
require('../../Transition/src/Collapse.js');
var useWindowSize = require('../hooks/useWindowSize.js');
var Carousel_module = require('../styles/Carousel.module.less.js');
var Arrows = require('./Arrows.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description A carousel component.
 *
 * @component
 * @example
 * ```jsx
 * <Carousel>
 *   <div>1</div>
 *   <div>2</div>
 * </Carousel>
 * ```
 */
const Carousel = React__namespace.forwardRef((props, ref) => {
    const { title, gradient, bottomControls, className, slidesClassName, gap = 20, children, ...restProps } = props;
    const dragControls = framerMotion.useDragControls();
    const cx = reactUtils.useStyles(Carousel_module.default);
    const [containerWidth, setContainerWidth] = React__namespace.useState(0);
    const [slidesWidth, setSlidesWidth] = React__namespace.useState(0);
    const [singleSlideWidth, setSingleSlideWidth] = React__namespace.useState(0);
    const [isPrevDisabled, setIsPrevDisabled] = React__namespace.useState(true);
    const [isNextDisabled, setIsNextDisabled] = React__namespace.useState(false);
    const containerRef = React__namespace.useRef(null);
    const xPos = React__namespace.useRef(0);
    const isMoveAvailable = slidesWidth > containerWidth;
    const isHeaderVisible = title || (isMoveAvailable && !bottomControls);
    const headerClassNames = cx('header', {
        hidden: !title,
    });
    const slidesWrapperClassNames = cx('slides-wrapper', {
        gradient,
        [`left-gradient`]: !isPrevDisabled,
        [`right-gradient`]: !isNextDisabled,
    });
    const animation = framerMotion.useAnimation();
    const width = useWindowSize.useWindowSize();
    const handlePrevClick = React__namespace.useCallback(async () => {
        const newXPosition = xPos.current + singleSlideWidth + gap;
        const constraint = 0;
        await animation.start({
            x: newXPosition > constraint ? constraint : newXPosition,
        }, {
            duration: 0.2,
            ease: _utils.EASINGS.easeOut,
        });
    }, [animation, singleSlideWidth, gap]);
    const handleNextClick = React__namespace.useCallback(async () => {
        const newXPosition = xPos.current - singleSlideWidth - gap;
        const constraint = containerWidth - slidesWidth;
        await animation.start({
            x: newXPosition < constraint ? constraint : newXPosition,
        }, {
            duration: 0.2,
            ease: _utils.EASINGS.easeOut,
        });
    }, [animation, singleSlideWidth, containerWidth, slidesWidth, gap]);
    const slides = React__namespace.useMemo(() => React__namespace.Children.map(children, (child) => (React__namespace.createElement("div", { style: { width: child.props.style?.width, height: child.props.style?.height } }, child))), [children]);
    const slidesStyle = React__namespace.useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '--gap': `${gap}px`,
        x: 0,
    }), [gap]);
    const handleUpdate = React__namespace.useCallback((latest) => {
        xPos.current = Number(latest.x);
        setIsPrevDisabled(xPos.current >= 0);
        setIsNextDisabled(xPos.current <= containerWidth - slidesWidth);
    }, [containerWidth, slidesWidth]);
    const preventPropagation = React__namespace.useCallback((event) => {
        event.stopPropagation();
        dragControls.start(event);
    }, [dragControls]);
    React__namespace.useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        const slider = containerRef.current;
        const newSlidesWidth = Array.from(slider.children).reduce((acc, slide) => acc + slide.clientWidth + gap, 0) - gap;
        setIsNextDisabled(Number(xPos?.current) <= containerWidth - newSlidesWidth);
        setSlidesWidth(newSlidesWidth);
        setContainerWidth(slider.clientWidth);
        setSingleSlideWidth(slider.children[0]?.clientWidth);
    }, [width, slides, containerWidth, gap]);
    return (React__namespace.createElement("div", { ref: ref, "data-qa": "Carousel", className: cx(className, 'container'), ...restProps },
        isHeaderVisible && (React__namespace.createElement(Space.Space, { justify: "space-between", className: headerClassNames },
            React__namespace.createElement("div", { className: cx('title') }, title),
            isMoveAvailable && !bottomControls && (React__namespace.createElement(Arrows.Arrows, { isPrevDisabled: isPrevDisabled, isNextDisabled: isNextDisabled, onPrevClick: handlePrevClick, onNextClick: handleNextClick })))),
        React__namespace.createElement("div", { className: slidesWrapperClassNames, onPointerDownCapture: preventPropagation },
            React__namespace.createElement(framerMotion.motion.div, { "data-qa": "Slides", ref: containerRef, drag: isMoveAvailable ? 'x' : false, animate: animation, initial: false, style: slidesStyle, dragControls: dragControls, dragConstraints: {
                    left: containerWidth - slidesWidth,
                    right: 0,
                }, onUpdate: handleUpdate, className: cx('slides', slidesClassName) }, slides)),
        isMoveAvailable && bottomControls && (React__namespace.createElement("div", { className: cx('bottom-controls') },
            React__namespace.createElement(Arrows.Arrows, { isPrevDisabled: isPrevDisabled, isNextDisabled: isNextDisabled, onPrevClick: handlePrevClick, onNextClick: handleNextClick })))));
});
Carousel.displayName = 'Carousel';

exports.Carousel = Carousel;
//# sourceMappingURL=Carousel.js.map
